import React, { useMemo } from 'react'
import { remark } from 'remark'
import strip from 'strip-markdown'

import { useMeta, useTitle } from 'hoofd'

import { DEFAULT_BACKGROUND } from '@data/config/images'

const IMAGE_TRANSFORM_PARAMS = {
  DEFAULT: {
    'og:image': '?w=1200',
    'twitter:image': '?w=1200&h=600&fit=cover',
  },
  FULL_SIZE: {
    'og:image': '?w=1200',
    'twitter:image': '?w=1200&h=600&fit=contain',
  },
  NONE: {
    'og:image': '',
    'twitter:image': '',
  },
} as const

export const Metadata = ({
  description,
  imageTransformParams = 'DEFAULT',
  photoUrl,
  title,
}: {
  description?: string | null
  imageTransformParams?: keyof typeof IMAGE_TRANSFORM_PARAMS
  photoUrl?: string | null
  title: string
}) => {
  const imageURL = useMemo(() => {
    return photoUrl ?? DEFAULT_BACKGROUND
  }, [photoUrl])

  const contentDescription = useMemo(() => {
    if (!description) {
      return
    }

    // @ts-expect-error strip-markdown types are incompatible with remark
    return String(remark().use(strip).processSync(description))
  }, [description])

  useTitle(`${title} | Guild`)
  useMeta({ property: 'og:title', content: `${title} | Guild` })

  useMeta({
    property: 'og:image',
    content: `${imageURL}${IMAGE_TRANSFORM_PARAMS[imageTransformParams]['og:image']}`,
  })

  useMeta({ property: 'twitter:title', content: title })
  useMeta({ property: 'twitter:card', content: 'summary_large_image' })
  useMeta({
    property: 'twitter:image',
    content: `${imageURL}${IMAGE_TRANSFORM_PARAMS[imageTransformParams]['twitter:image']}`,
  })

  return useMemo(
    () =>
      contentDescription ? (
        <MetadataDescription description={contentDescription} />
      ) : null,
    [contentDescription]
  )
}

const MetadataDescription = ({ description }: { description: string }) => {
  const shortenedDescription = description.split('\n').slice(0, 4).join('\n')

  useMeta({
    property: 'description',
    content: shortenedDescription,
  })
  useMeta({
    property: 'og:description',
    content: description.substring(0, 140),
  })
  useMeta({ property: 'twitter:description', content: shortenedDescription })

  return null
}
